/* eslint-disable */
/**
 * Centralized image constants for consistent placeholder images across the application
 */

// Standard placeholder image for properties with no image
// This is a base64-encoded SVG showing a camera/image icon
export const PROPERTY_PLACEHOLDER_IMAGE = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODgiIGhlaWdodD0iODgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSIjMDAwIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBvcGFjaXR5PSIuMyIgZmlsbD0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIzLjciPjxyZWN0IHg9IjE2IiB5PSIxNiIgd2lkdGg9IjU2IiBoZWlnaHQ9IjU2IiByeD0iNiIvPjxwYXRoIGQ9Im0xNiA1OCAxNi0xOCAzMiAzMiIvPjxjaXJjbGUgY3g9IjUzIiBjeT0iMzUiIHI9IjciLz48L3N2Zz4K";

const buildPropertyImageProxyUrl = (propertyId, fileId) => {
  if (!propertyId || !fileId) return null;
  return `/project-management-service/api/v1/properties/${encodeURIComponent(String(propertyId))}/images/${encodeURIComponent(String(fileId))}`;
};

const getBackendOrigin = () => {
  const envBase = process.env.REACT_APP_API_URL;
  if (!envBase) return null;

  try {
    const parsed = new URL(envBase);
    return parsed.origin;
  } catch (e) {
    try {
      const parsed = new URL(envBase, window.location.origin);
      return parsed.origin;
    } catch (ignore) {
      return null;
    }
  }
};

export const normalizeMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;

  const backendOrigin = getBackendOrigin();

  // Relative paths from API payloads should resolve against backend host.
  if (url.startsWith('/')) {
    // Property image endpoints arrive as /api/v1/... and should stay same-origin
    // to avoid CORP/CORB blocking in img tags when frontend/backend ports differ.
    if (url.startsWith('/api/')) {
      return `/project-management-service${url}`;
    }

    return backendOrigin ? `${backendOrigin}${url}` : `${window.location.origin}${url}`;
  }

  // Protocol-relative URLs.
  if (url.startsWith('//')) {
    return `${window.location.protocol}${url}`;
  }

  try {
    const parsed = new URL(url);
    const isLocalHost = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
    const fullRelativePath = `${parsed.pathname}${parsed.search || ''}${parsed.hash || ''}`;

    // Force same-origin for backend media endpoints to avoid CORP NotSameOrigin blocking.
    if (parsed.pathname.startsWith('/project-management-service/api/')) {
      return fullRelativePath;
    }

    if (parsed.pathname.startsWith('/api/')) {
      return `/project-management-service${fullRelativePath}`;
    }

    // On a physical device, localhost points to the phone itself; swap to current host.
    if (isLocalHost && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      parsed.hostname = window.location.hostname;
      return parsed.toString();
    }

    // Avoid mixed content issues when app is served over HTTPS.
    if (window.location.protocol === 'https:' && parsed.protocol === 'http:' && !isLocalHost) {
      parsed.protocol = 'https:';
      return parsed.toString();
    }
  } catch (e) {
    // Non-URL strings are returned unchanged.
  }

  return url;
};

/**
 * Converts Google Drive URLs to a format that displays properly in img tags
 * Uses lh3.googleusercontent.com which is Google's CDN and works immediately
 * @param {string} url - The Google Drive URL
 * @returns {string} - The converted URL
 */
export const convertGoogleDriveUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return url;
  }

  const normalizedUrl = normalizeMediaUrl(url);

  // If it's already a base64 data URL, return as-is
  if (normalizedUrl.startsWith('data:')) {
    return normalizedUrl;
  }

  // If it's already using lh3.googleusercontent.com, return as-is
  if (normalizedUrl.includes('lh3.googleusercontent.com')) {
    return normalizedUrl;
  }

  // Check if it's a Google Drive URL that needs conversion
  if (normalizedUrl.includes('drive.google.com')) {
    // Extract file ID from various Google Drive URL formats
    let fileId = null;

    // Pattern 1: /uc?export=download&id=FILE_ID or /uc?id=FILE_ID&export=download
    const ucMatch = normalizedUrl.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (ucMatch) {
      fileId = ucMatch[1];
    }

    // Pattern 2: /d/FILE_ID/ or /file/d/FILE_ID/
    const dMatch = normalizedUrl.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (!fileId && dMatch) {
      fileId = dMatch[1];
    }

    // Pattern 3: /thumbnail?id=FILE_ID
    const thumbMatch = normalizedUrl.match(/thumbnail\?id=([a-zA-Z0-9_-]+)/);
    if (!fileId && thumbMatch) {
      fileId = thumbMatch[1];
    }

    // If we extracted a file ID, convert to lh3.googleusercontent.com format
    // This is Google's CDN and works immediately for public files
    if (fileId) {
      return `https://lh3.googleusercontent.com/d/${fileId}`;
    }
  }

  // Return original URL if it's not a Google Drive URL or couldn't parse
  return normalizedUrl;
};

/**
 * Converts Google Drive video URLs to streamable/downloadable URLs for HTML video tags.
 * @param {string} url - The Google Drive URL
 * @returns {string} - Streamable URL
 */
export const convertGoogleDriveVideoUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return url;
  }

  const normalizedUrl = normalizeMediaUrl(url);

  if (normalizedUrl.startsWith('data:') || normalizedUrl.startsWith('blob:')) {
    return normalizedUrl;
  }

  if (normalizedUrl.includes('drive.google.com')) {
    let fileId = null;

    const ucMatch = normalizedUrl.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (ucMatch) {
      fileId = ucMatch[1];
    }

    const dMatch = normalizedUrl.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (!fileId && dMatch) {
      fileId = dMatch[1];
    }

    if (fileId) {
      return `https://drive.google.com/uc?export=view&id=${fileId}`;
    }
  }

  return normalizedUrl;
};

/**
 * Gets the first valid image from a property object
 * @param {Object} property - The property object
 * @param {Function} getProp - Helper function to get property values (handles camelCase and snake_case)
 * @returns {string} - The image URL or placeholder
 */
export const getPropertyImage = (property, getProp) => {
  const isLikelyUrl = (value) => {
    if (!value || typeof value !== 'string') return false;
    return (
      value.startsWith('http://') ||
      value.startsWith('https://') ||
      value.startsWith('/') ||
      value.startsWith('data:') ||
      value.startsWith('blob:')
    );
  };

  const parseArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  const toUrl = (item, propertyId) => {
    if (!item) return null;
    if (typeof item === 'string') {
      // Some payloads return file IDs as plain strings after media processing.
      return isLikelyUrl(item) ? item : buildPropertyImageProxyUrl(propertyId, item);
    }
    if (typeof item === 'object') {
      const directUrl = item.preview || item.uploadUrl || item.gcsUrl || item.url || item.path || null;
      if (directUrl) return directUrl;

      const fileId =
        item.id ||
        item.fileId ||
        item.file_id ||
        item.image_id ||
        item.imageId ||
        item.property_image_id ||
        item.drive_file_id ||
        item.driveFileId;

      return buildPropertyImageProxyUrl(propertyId, fileId);
    }
    return null;
  };

  const propertyId =
    property?.property_id ||
    property?.propertyId ||
    property?.id ||
    null;

  const isVideoItem = (item) => {
    if (!item || typeof item === 'string') return false;
    if (item.mediaType === 'video') return true;
    if (item.type && typeof item.type === 'string' && item.type.startsWith('video/')) return true;
    return false;
  };

  // Prefer property_images metadata ordering to stay consistent with logged-in card media selection.
  const images = parseArray(getProp('images', 'images', []));
  const propertyImages = parseArray(
    property?.property_images || property?.propertyImages || []
  );

  // Skip video items — pick the first image entry even if a video comes before it
  const firstNonVideoImage = images.find(item => !isVideoItem(item));
  const firstImageFromImages = firstNonVideoImage ? toUrl(firstNonVideoImage, propertyId) : null;
  const firstNonVideoPropertyImage = propertyImages.find(item => !isVideoItem(item));
  const firstImageFromPropertyImages = firstNonVideoPropertyImage ? toUrl(firstNonVideoPropertyImage, propertyId) : null;

  // Fallback to direct image property or placeholder
  const imageUrl =
    firstImageFromPropertyImages ||
    firstImageFromImages ||
    getProp('image', 'image', null);

  // Convert Google Drive URLs to displayable format
  if (imageUrl) {
    return convertGoogleDriveUrl(imageUrl);
  }

  return PROPERTY_PLACEHOLDER_IMAGE;
};

/**
 * Image error handler with retry mechanism for Google Drive images
 * Retries loading the image up to 3 times with increasing delays
 * @param {Event} e - The error event
 * @param {number} maxRetries - Maximum number of retries (default: 3)
 */
export const handleImageError = (e, maxRetries = 3) => {
  const img = e.target;
  const currentRetry = parseInt(img.dataset.retryCount || '0', 10);
  const originalSrc = img.dataset.originalSrc || img.src;

  // Store original source on first error
  if (!img.dataset.originalSrc) {
    img.dataset.originalSrc = img.src;
  }

  // Check if it's a Google Drive URL or lh3 URL that might need retry
  const isGoogleDriveUrl = originalSrc.includes('drive.google.com') || originalSrc.includes('lh3.googleusercontent.com');

  if (isGoogleDriveUrl && currentRetry < maxRetries) {
    // Increment retry count
    img.dataset.retryCount = String(currentRetry + 1);

    // Calculate delay with exponential backoff (1s, 2s, 4s)
    const delay = Math.pow(2, currentRetry) * 1000;

    // Set placeholder temporarily
    img.src = PROPERTY_PLACEHOLDER_IMAGE;

    // Retry after delay - convert to lh3 format if needed and add cache-buster
    setTimeout(() => {
      // Convert URL to lh3 format (in case it was using old format)
      const lh3Url = convertGoogleDriveUrl(originalSrc);
      const cacheBuster = `?_t=${Date.now()}`;
      img.src = lh3Url.includes('?')
        ? `${lh3Url}&_t=${Date.now()}`
        : `${lh3Url}${cacheBuster}`;
    }, delay);
  } else {
    // Max retries reached or not a Google Drive URL, use placeholder
    img.src = PROPERTY_PLACEHOLDER_IMAGE;
    // Clear retry data
    delete img.dataset.retryCount;
    delete img.dataset.originalSrc;
  }
};

/**
 * Preload an image to check if it's accessible
 * @param {string} url - The image URL to preload
 * @param {number} timeout - Timeout in milliseconds (default: 5000)
 * @returns {Promise<boolean>} - Returns true if image loads successfully
 */
export const preloadImage = (url, timeout = 5000) => {
  return new Promise((resolve) => {
    const img = new Image();
    const timer = setTimeout(() => {
      resolve(false);
    }, timeout);

    img.onload = () => {
      clearTimeout(timer);
      resolve(true);
    };

    img.onerror = () => {
      clearTimeout(timer);
      resolve(false);
    };

    img.src = url;
  });
};
